/**
 * Currency Input Component for AkountFly
 * 
 * Features:
 * - Formats numbers with comma separators as user types
 * - Shows currency symbol
 * - Handles decimal places
 * - Returns raw numeric value for form submission
 */

import React, { useState, useEffect, useRef, forwardRef } from 'react';
import { Input } from './input';
import { cn } from '../../lib/utils';

/**
 * Format a number with thousand separators
 * @param {string|number} value - The value to format
 * @param {number} decimals - Number of decimal places (default: 2)
 * @returns {string} Formatted string with commas
 */
export const formatNumber = (value, decimals = 2) => {
  if (!value && value !== 0) return '';
  
  // Convert to string and remove existing commas
  const strValue = String(value).replace(/,/g, '');
  
  // Parse to number
  const num = parseFloat(strValue);
  if (isNaN(num)) return '';
  
  // Format with commas
  const parts = num.toFixed(decimals).split('.');
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  
  // Don't show decimals if they're .00
  if (parts[1] === '00') {
    return parts[0];
  }
  
  return parts.join('.');
};

/**
 * Format number for display (with commas, no trailing zeros)
 */
export const formatDisplayNumber = (value) => {
  if (!value && value !== 0) return '';
  
  const strValue = String(value).replace(/,/g, '');
  const num = parseFloat(strValue);
  if (isNaN(num)) return '';
  
  // Use toLocaleString for proper formatting
  return num.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  });
};

/**
 * Parse formatted string back to number
 */
export const parseFormattedNumber = (value) => {
  if (!value) return '';
  const strValue = String(value).replace(/,/g, '');
  const num = parseFloat(strValue);
  return isNaN(num) ? '' : num;
};

const CurrencyInput = forwardRef(({
  value,
  onChange,
  currencySymbol = '₦',
  placeholder = '0.00',
  className,
  showSymbol = true,
  decimals = 2,
  allowNegative = false,
  maxValue,
  minValue = 0,
  disabled = false,
  ...props
}, ref) => {
  const [displayValue, setDisplayValue] = useState('');
  const [isFocused, setIsFocused] = useState(false);

  // Sync display value with external value
  useEffect(() => {
    if (!isFocused) {
      setDisplayValue(value ? formatDisplayNumber(value) : '');
    }
  }, [value, isFocused]);

  const handleChange = (e) => {
    let inputValue = e.target.value;
    
    // Remove all non-numeric characters except decimal point and minus
    let cleanValue = inputValue.replace(/[^0-9.-]/g, '');
    
    // Handle negative numbers
    if (!allowNegative) {
      cleanValue = cleanValue.replace(/-/g, '');
    }
    
    // Ensure only one decimal point
    const decimalCount = (cleanValue.match(/\./g) || []).length;
    if (decimalCount > 1) {
      const parts = cleanValue.split('.');
      cleanValue = parts[0] + '.' + parts.slice(1).join('');
    }
    
    // Parse to number
    const numValue = parseFloat(cleanValue);
    
    // Validate against max/min
    if (!isNaN(numValue)) {
      if (maxValue !== undefined && numValue > maxValue) {
        cleanValue = String(maxValue);
      }
      if (minValue !== undefined && numValue < minValue) {
        cleanValue = String(minValue);
      }
    }
    
    // Format for display
    if (cleanValue) {
      const parts = cleanValue.split('.');
      // Add commas to integer part
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      setDisplayValue(parts.join('.'));
    } else {
      setDisplayValue('');
    }
    
    // Call onChange with numeric value
    const numericValue = parseFloat(cleanValue);
    onChange?.(isNaN(numericValue) ? '' : numericValue);
  };

  // Select-all on focus synchronously (a deferred select() used to swallow the first digit typed quickly);
  // the mouseup that follows a click would clear the selection, so suppress that one mouseup.
  const suppressMouseUpRef = useRef(false);
  const handleFocus = (e) => {
    setIsFocused(true);
    e.target.select();
    suppressMouseUpRef.current = true;
  };
  const handleMouseUp = (e) => {
    if (suppressMouseUpRef.current) {
      e.preventDefault();
      suppressMouseUpRef.current = false;
    }
  };

  const handleBlur = () => {
    setIsFocused(false);
    // Format with proper decimals on blur
    if (displayValue) {
      const num = parseFormattedNumber(displayValue);
      if (num !== '') {
        setDisplayValue(formatDisplayNumber(num));
      }
    }
  };

  return (
    <div className="relative">
      {showSymbol && (
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground font-medium z-10">
          {currencySymbol}
        </span>
      )}
      <Input
        ref={ref}
        type="text"
        inputMode="decimal"
        value={displayValue}
        onChange={handleChange}
        onFocus={handleFocus}
        onMouseUp={handleMouseUp}
        onBlur={handleBlur}
        placeholder={placeholder}
        disabled={disabled}
        className={cn(
          showSymbol && 'pl-10',
          'text-right font-medium',
          className
        )}
        {...props}
      />
    </div>
  );
});

CurrencyInput.displayName = 'CurrencyInput';

export { CurrencyInput };
export default CurrencyInput;
